import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { BarChart3, TrendingUp, DollarSign, ShoppingCart, ArrowLeft } from 'lucide-react';
import Card from '@/components/ui/Card';
import Select from '@/components/ui/Select';
import Badge from '@/components/ui/Badge';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import api from '@/lib/api';

interface ChannelMetric {
  channel_id: string;
  channel_name: string;
  platform: string;
  total_orders: number;
  total_revenue: number;
  total_fees: number;
  total_clicks: number;
  total_impressions: number;
  total_items_sold: number;
}

interface DailyMetric {
  date: string;
  orders: number;
  revenue: number;
  clicks: number;
  impressions: number;
}

interface AnalyticsTotals {
  total_orders: number;
  total_revenue: number;
  total_fees: number;
  total_items_sold: number;
  total_clicks: number;
  total_impressions: number;
}

interface AnalyticsResponse {
  period: { days: number; since: string };
  totals: AnalyticsTotals;
  channels: ChannelMetric[];
  daily: DailyMetric[];
}

const PLATFORM_LABELS: Record<string, string> = {
  google_shopping: 'Google Shopping',
  facebook_shopping: 'Facebook/Instagram',
  amazon: 'Amazon',
  ebay: 'eBay',
  etsy: 'Etsy',
  walmart: 'Walmart',
  tiktok_shop: 'TikTok Shop',
};

const PLATFORM_COLORS: Record<string, string> = {
  google_shopping: '#4285F4',
  facebook_shopping: '#1877F2',
  amazon: '#FF9900',
  ebay: '#E53238',
  etsy: '#F56400',
  walmart: '#0071CE',
  tiktok_shop: '#FF004F',
};

const DATE_RANGES = [
  { label: 'Last 7 days', value: '7' },
  { label: 'Last 30 days', value: '30' },
  { label: 'Last 90 days', value: '90' },
  { label: 'Last 365 days', value: '365' },
];

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n);
}

/** Simple bar chart using CSS. */
function BarChartSimple({ data, valueKey, maxValue }: { data: ChannelMetric[]; valueKey: keyof ChannelMetric; maxValue: number }) {
  if (data.length === 0 || maxValue === 0) {
    return <p className="text-sm text-gray-400 py-4 text-center">No data for this period</p>;
  }
  return (
    <div className="space-y-3">
      {data.map((ch) => {
        const val = Number(ch[valueKey]) || 0;
        const pct = maxValue > 0 ? (val / maxValue) * 100 : 0;
        return (
          <div key={ch.channel_id}>
            <div className="flex justify-between text-sm mb-1">
              <span className="font-medium text-gray-700">{ch.channel_name}</span>
              <span className="text-gray-500">
                {valueKey === 'total_revenue' || valueKey === 'total_fees' ? formatCurrency(val) : formatNumber(val)}
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-3">
              <div
                className="h-3 rounded-full transition-all"
                style={{ width: `${Math.max(pct, 1)}%`, backgroundColor: PLATFORM_COLORS[ch.platform] || '#6B7280' }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

const MarketplaceAnalytics = () => {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState('30');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await api.get<AnalyticsResponse>(`/admin/marketplace-analytics?days=${days}`);
        setData(response);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load analytics');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [days]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  const totals = data?.totals || { total_orders: 0, total_revenue: 0, total_fees: 0, total_items_sold: 0, total_clicks: 0, total_impressions: 0 };
  const channels = data?.channels || [];
  const netRevenue = Number(totals.total_revenue) - Number(totals.total_fees);
  const maxRevenue = Math.max(...channels.map((c) => Number(c.total_revenue)), 1);
  const maxOrders = Math.max(...channels.map((c) => Number(c.total_orders)), 1);

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/admin/marketplace" className="p-2 hover:bg-stone-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Channel Analytics</h1>
            <p className="text-gray-600 mt-1">Performance metrics across selling channels</p>
          </div>
        </div>
        <div className="w-48">
          <Select
            value={days}
            onChange={(e) => setDays(e.target.value)}
            options={DATE_RANGES}
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800 text-sm">{error}</div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <DollarSign className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(Number(totals.total_revenue))}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShoppingCart className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Orders</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(totals.total_orders)}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <TrendingUp className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Net Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(netRevenue)}</p>
            </div>
          </div>
        </Card>
        <Card padding="md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <BarChart3 className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Items Sold</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(totals.total_items_sold)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Channel Comparison */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card title="Revenue by Channel" padding="md">
          <BarChartSimple data={channels} valueKey="total_revenue" maxValue={maxRevenue} />
        </Card>
        <Card title="Orders by Channel" padding="md">
          <BarChartSimple data={channels} valueKey="total_orders" maxValue={maxOrders} />
        </Card>
      </div>

      {/* Channel Breakdown Table */}
      <Card title="Channel Breakdown" padding="md">
        {channels.length === 0 ? (
          <p className="text-center py-6 text-gray-500">No analytics data yet. Data populates as marketplace orders come in.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Channel</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-stone-500 uppercase">Platform</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Revenue</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Fees</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Net</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Orders</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Items</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-stone-500 uppercase">Clicks</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {channels.map((ch) => (
                  <tr key={ch.channel_id} className="hover:bg-stone-50">
                    <td className="px-4 py-3">
                      <Link to={`/admin/marketplace/channels/${ch.channel_id}`} className="font-medium text-gray-900 hover:text-blue-600">
                        {ch.channel_name}
                      </Link>
                    </td>
                    <td className="px-4 py-3">
                      <Badge variant="gray">{PLATFORM_LABELS[ch.platform] || ch.platform}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-medium">{formatCurrency(Number(ch.total_revenue))}</td>
                    <td className="px-4 py-3 text-right text-sm text-red-600">{formatCurrency(Number(ch.total_fees))}</td>
                    <td className="px-4 py-3 text-right text-sm font-medium text-green-600">
                      {formatCurrency(Number(ch.total_revenue) - Number(ch.total_fees))}
                    </td>
                    <td className="px-4 py-3 text-right text-sm">{formatNumber(ch.total_orders)}</td>
                    <td className="px-4 py-3 text-right text-sm">{formatNumber(ch.total_items_sold)}</td>
                    <td className="px-4 py-3 text-right text-sm">{formatNumber(ch.total_clicks)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
};

export default MarketplaceAnalytics;
